import { isDeciduousTooth } from './odontology';

/**
 * Silhuetas laterais dos dentes para o odontograma.
 *
 * Todos os desenhos usam o mesmo sistema de coordenadas — viewBox 100 × 200,
 * ápice radicular no topo e borda incisal/oclusal embaixo — para que a linha
 * cervical de dentes diferentes caia na mesma altura quando alinhados lado a
 * lado. Dentes inferiores são o mesmo desenho refletido no eixo Y, e a
 * hemiarcada esquerda é refletida no eixo X, seguindo a convenção de
 * desdobramento dos quatro quadrantes.
 *
 * Os contornos são desenhos originais, esquemáticos: a proporção entre coroa,
 * raiz e câmara pulpar é o que importa clinicamente aqui, não o detalhe
 * anatômico de um atlas.
 */

export interface ToothApex {
  x: number;
  y: number;
}

export interface ToothAnatomy {
  /** Contorno da coroa, do colo à borda incisal/oclusal. */
  crown: string;
  /** Uma entrada por raiz. */
  roots: string[];
  /** Câmara pulpar, com os cornos voltados às cúspides. */
  pulpChamber: string;
  /** Eixo de cada canal radicular, traçado (não preenchido). */
  canals: string[];
  /** Ponta de cada raiz, onde ancoram lesões periapicais e apicectomias. */
  apices: ToothApex[];
  /** Altura da junção cemento-esmalte — limite entre coroa e raiz. */
  cej: number;
  /** Largura relativa do glifo; molares ocupam mais espaço que incisivos. */
  widthRatio: number;
}

export const LATERAL_VIEW_BOX = '0 0 100 200';

/** Posição dentro do quadrante: 1 = incisivo central … 8 = terceiro molar. */
export type ToothPosition = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

const ANATOMY_BY_POSITION: Record<ToothPosition, ToothAnatomy> = {
  // Incisivo central — coroa em pá, raiz única cônica.
  1: {
    cej: 122,
    widthRatio: 0.62,
    crown:
      'M 34,122 C 31,142 30,162 33,181 C 34,190 38,193 43,193 L 57,193 C 62,193 66,190 67,181 C 70,162 69,142 66,122 Z',
    roots: ['M 34,122 C 35,92 37,52 45,17 C 47,10 53,10 55,17 C 63,52 65,92 66,122 Z'],
    pulpChamber: 'M 43,124 L 57,124 C 58,134 58,145 55,152 C 52,158 48,158 45,152 C 42,145 42,134 43,124 Z',
    canals: ['M 50,124 C 50,96 50,58 50,26'],
    apices: [{ x: 50, y: 17 }],
  },

  // Incisivo lateral — mesmo padrão do central, mais estreito.
  2: {
    cej: 124,
    widthRatio: 0.56,
    crown:
      'M 37,124 C 34,143 33,163 36,181 C 37,190 41,193 45,193 L 55,193 C 59,193 63,190 64,181 C 67,163 66,143 63,124 Z',
    roots: ['M 37,124 C 38,95 40,56 46,21 C 48,14 52,14 54,21 C 60,56 62,95 63,124 Z'],
    pulpChamber: 'M 45,126 L 55,126 C 56,135 56,145 54,151 C 52,157 48,157 46,151 C 44,145 44,135 45,126 Z',
    canals: ['M 50,126 C 50,98 50,62 50,30'],
    apices: [{ x: 50, y: 21 }],
  },

  // Canino — cúspide única pontiaguda, raiz longa e robusta.
  3: {
    cej: 120,
    widthRatio: 0.62,
    crown:
      'M 34,120 C 31,142 31,164 36,180 L 47,196 C 49,198 51,198 53,196 L 64,180 C 69,164 69,142 66,120 Z',
    roots: ['M 34,120 C 35,86 37,44 45,12 C 47,5 53,5 55,12 C 63,44 65,86 66,120 Z'],
    pulpChamber: 'M 44,122 L 56,122 C 57,133 57,146 54,154 C 52,161 48,161 46,154 C 43,146 43,133 44,122 Z',
    canals: ['M 50,122 C 50,88 50,48 50,20'],
    apices: [{ x: 50, y: 12 }],
  },

  // Primeiro pré-molar — duas cúspides, raiz única com sulco longitudinal.
  4: {
    cej: 126,
    widthRatio: 0.68,
    crown:
      'M 33,126 C 30,146 30,166 33,178 L 39,190 C 41,194 45,194 47,190 L 50,183 L 53,190 C 55,194 59,194 61,190 L 67,178 C 70,166 70,146 67,126 Z',
    roots: ['M 33,126 C 34,96 36,58 44,22 C 47,15 53,15 56,22 C 64,58 66,96 67,126 Z'],
    pulpChamber:
      'M 42,130 L 58,130 C 59,139 58,148 55,153 C 53,157 52,158 51,155 L 50,150 L 49,155 C 48,158 47,157 45,153 C 42,148 41,139 42,130 Z',
    canals: ['M 50,130 C 50,100 50,62 50,26'],
    apices: [{ x: 50, y: 21 }],
  },

  // Segundo pré-molar — cúspides mais equilibradas, raiz única.
  5: {
    cej: 126,
    widthRatio: 0.68,
    crown:
      'M 34,126 C 31,146 31,166 34,178 L 40,189 C 42,193 46,193 48,189 L 50,184 L 52,189 C 54,193 58,193 60,189 L 66,178 C 69,166 69,146 66,126 Z',
    roots: ['M 34,126 C 35,96 37,60 45,24 C 47,17 53,17 55,24 C 63,60 65,96 66,126 Z'],
    pulpChamber:
      'M 43,130 L 57,130 C 58,139 57,148 54,153 C 52,157 51,158 50,155 L 50,151 L 50,155 C 49,158 48,157 46,153 C 43,148 42,139 43,130 Z',
    canals: ['M 50,130 C 50,100 50,64 50,28'],
    apices: [{ x: 50, y: 23 }],
  },

  // Primeiro molar — coroa larga de quatro cúspides, raízes divergentes.
  6: {
    cej: 128,
    widthRatio: 1,
    crown:
      'M 16,128 C 13,150 14,170 19,180 C 22,187 27,190 31,187 L 37,180 C 41,187 45,189 50,189 C 55,189 59,187 63,180 L 69,187 C 73,190 78,187 81,180 C 86,170 87,150 84,128 Z',
    roots: [
      'M 20,128 C 19,98 15,58 17,28 C 18,18 26,18 28,28 C 34,58 38,98 40,128 Z',
      'M 60,128 C 62,98 66,58 72,28 C 74,18 82,18 83,28 C 85,58 81,98 80,128 Z',
    ],
    pulpChamber:
      'M 30,132 L 70,132 C 71,144 69,154 64,160 C 61,163 60,158 58,152 C 56,158 54,162 50,162 C 46,162 44,158 42,152 C 40,158 39,163 36,160 C 31,154 29,144 30,132 Z',
    canals: ['M 34,132 C 32,104 26,64 23,30', 'M 66,132 C 68,104 74,64 77,30'],
    apices: [{ x: 22, y: 24 }, { x: 78, y: 24 }],
  },

  // Segundo molar — coroa ligeiramente menor, raízes mais paralelas.
  7: {
    cej: 130,
    widthRatio: 0.95,
    crown:
      'M 18,130 C 15,150 16,170 21,180 C 24,186 29,189 33,186 L 38,180 C 42,186 46,188 50,188 C 54,188 58,186 62,180 L 67,186 C 71,189 76,186 79,180 C 84,170 85,150 82,130 Z',
    roots: [
      'M 21,130 C 20,102 18,64 21,34 C 22,24 30,24 32,34 C 36,64 39,102 40,130 Z',
      'M 60,130 C 61,102 64,64 68,34 C 70,24 78,24 79,34 C 82,64 80,102 79,130 Z',
    ],
    pulpChamber:
      'M 31,134 L 69,134 C 70,145 68,155 63,160 C 60,163 59,158 57,153 C 55,158 53,161 50,161 C 47,161 45,158 43,153 C 41,158 40,163 37,160 C 32,155 30,145 31,134 Z',
    canals: ['M 35,134 C 33,106 28,70 26,36', 'M 65,134 C 67,106 72,70 74,36'],
    apices: [{ x: 26, y: 30 }, { x: 74, y: 30 }],
  },

  // Terceiro molar — coroa irregular e raízes curtas, com frequência fusionadas.
  8: {
    cej: 132,
    widthRatio: 0.88,
    crown:
      'M 22,132 C 19,150 20,168 25,178 C 28,184 33,186 37,183 L 41,178 C 44,183 47,185 50,185 C 53,185 56,183 59,178 L 63,183 C 67,186 72,184 75,178 C 80,168 81,150 78,132 Z',
    roots: [
      'M 25,132 C 24,110 24,86 28,64 C 30,54 37,54 39,64 C 41,86 41,110 42,132 Z',
      'M 58,132 C 59,110 59,86 61,64 C 63,54 70,54 72,64 C 76,86 76,110 75,132 Z',
    ],
    pulpChamber:
      'M 34,136 L 66,136 C 67,146 65,154 61,158 C 58,161 57,157 55,153 C 53,157 52,159 50,159 C 48,159 47,157 45,153 C 43,157 42,161 39,158 C 35,154 33,146 34,136 Z',
    canals: ['M 38,136 C 36,112 34,86 33,62', 'M 62,136 C 64,112 66,86 67,62'],
    apices: [{ x: 33, y: 58 }, { x: 67, y: 58 }],
  },
};

/**
 * Decíduos reaproveitam a silhueta do permanente equivalente: os molares
 * decíduos (posições 4 e 5) têm forma de molar, não de pré-molar.
 */
const DECIDUOUS_SHAPE: Record<number, ToothPosition> = { 1: 1, 2: 2, 3: 3, 4: 6, 5: 7 };

/** Decíduos são desenhados menores que os permanentes. */
export const DECIDUOUS_SCALE = 0.82;

function shapePosition(toothNumber: string): ToothPosition {
  const index = Number(toothNumber.charAt(1));
  if (isDeciduousTooth(toothNumber)) return DECIDUOUS_SHAPE[index] ?? 1;
  return index >= 1 && index <= 8 ? (index as ToothPosition) : 1;
}

export function getToothAnatomy(toothNumber: string): ToothAnatomy {
  return ANATOMY_BY_POSITION[shapePosition(toothNumber)];
}

/**
 * Implante: a fixação rosqueada substitui a raiz e o munhão emerge no colo.
 * Mesmas coordenadas da silhueta, ancoradas na linha cervical.
 */
export const IMPLANT_SHAPE = {
  fixture: 'M 40,120 L 60,120 L 57,58 C 56,44 44,44 43,58 Z',
  threads: [
    'M 41,108 L 59,108',
    'M 42,96 L 58,96',
    'M 43,84 L 57,84',
    'M 44,72 L 56,72',
    'M 45,60 L 55,60',
  ],
  abutment: 'M 43,136 L 57,136 L 59,120 L 41,120 Z',
};

/** Pino intrarradicular, desenhado sobre o canal. */
export const POST_SHAPE = 'M 46,138 L 54,138 L 52,66 L 48,66 Z';
